import { db, generateId } from './FishingDB';
import { Species } from '../entities/SpeciesEntities';
import { getSpeciesMetadata, getSpeciesAIProfile, SpeciesProfile } from '@fishing-app/shared';

// Extended species with translation and AI profile
export interface SpeciesWithTranslation extends Species {
  name: string; // Translated name in current language
  aiProfile?: SpeciesProfile; // AI profile for scoring
}

// Get all species (default + custom) with translations
export async function getAllSpecies(
  t: (key: string) => string, // Translation function from i18n
  currentLanguage: string
): Promise<SpeciesWithTranslation[]> {
  const dbSpecies = await db.species.toArray();
  
  return dbSpecies.map(species => {
    const metadata = getSpeciesMetadata(species.key);
    return {
      ...species,
      name: species.isCustom ? species.key : t(species.key), // Custom = key is the name
      aiProfile: metadata?.aiProfile || getSpeciesAIProfile('general') // Fallback to general
    };
  });
}

// Get species by key with translation
export async function getSpeciesByKey(
  key: string,
  t: (key: string) => string
): Promise<SpeciesWithTranslation | undefined> {
  const species = await db.species.where('key').equals(key).first();
  if (!species) return undefined;

  const metadata = getSpeciesMetadata(species.key);
  return {
    ...species,
    name: species.isCustom ? species.key : t(species.key),
    aiProfile: metadata?.aiProfile || getSpeciesAIProfile('general')
  };
}

// Get translated name for species key
export function getSpeciesName(key: string, t: (key: string) => string, isCustom: boolean = false): string {
  return isCustom ? key : t(key);
}

// Add custom species (key = name in current language, uses general AI profile)
export async function addCustomSpecies(data: {
  name: string; // Name in current language (will be stored as key)
  icon?: string;
  isLargeSpecies?: boolean;
}): Promise<Species> {
  // For custom species, the name IS the key (no transformation)
  const key = data.name;
  
  // Check if key already exists
  const existing = await db.species.where('key').equals(key).first();
  if (existing) {
    throw new Error('Species already exists');
  }

  const newSpecies: Species = {
    id: generateId(),
    key, // Custom species: key = name in current language
    icon: data.icon || '🐟',
    isCustom: true,
    isLargeSpecies: data.isLargeSpecies ?? false
  };

  await db.species.add(newSpecies);
  console.log(`✅ Added custom species: ${data.name}`);
  return newSpecies;
}

// Update custom species
export async function updateCustomSpecies(id: string, updates: {
  name?: string; // Updates the key (since key = name for custom species)
  icon?: string;
  isLargeSpecies?: boolean;
}): Promise<void> {
  const species = await db.species.get(id);
  if (!species) {
    throw new Error('Species not found');
  }

  if (!species.isCustom) {
    throw new Error('Cannot modify default species');
  }

  const updateData: Partial<Species> = {};
  
  if (updates.name) {
    updateData.key = updates.name; // Update key since key = name
  }

  if (updates.icon !== undefined) {
    updateData.icon = updates.icon;
  }

  if (updates.isLargeSpecies !== undefined) {
    updateData.isLargeSpecies = updates.isLargeSpecies;
  }

  await db.species.update(id, updateData);
  console.log(`✅ Updated custom species: ${id}`);
}

// Delete custom species
export async function deleteCustomSpecies(id: string): Promise<void> {
  const species = await db.species.get(id);
  if (!species) {
    throw new Error('Species not found');
  }

  if (!species.isCustom) {
    throw new Error('Cannot delete default species');
  }

  await db.species.delete(id);
  console.log(`✅ Deleted custom species: ${id}`);
}

// Check if species is large (for quick-select logic)
export async function isLargeSpecies(key: string): Promise<boolean> {
  const species = await db.species.where('key').equals(key).first();
  if (!species) {
    // Check metadata for default species
    const metadata = getSpeciesMetadata(key);
    return metadata?.isLargeSpecies || false;
  }
  return species.isLargeSpecies || false;
}
